/**
 * Pure autosave decision helpers for the Report Builder editor.
 *
 * These exist as standalone functions (rather than inline in the provider)
 * so the autosave state machine can be unit-tested without mounting the
 * docx editor or the React context. The docx editor fires `onChange` for
 * non-content mutations (`setZoom` on every ResizeObserver tick / column
 * toggle, selection, layout/pagination); the signature comparison here is
 * what stops those from triggering a save (which kept resetting `lastSavedAt`
 * and looping the "Saving…/Autosaved just now" label).
 */

type SigMark = { type: { name: string }; attrs?: Record<string, unknown> | null };
type SigNode = {
  isText?: boolean;
  text?: string;
  marks?: readonly SigMark[];
  attrs?: Record<string, unknown> | null;
  type?: { name: string };
};
type SigDoc = {
  content: { size: number };
  textContent: string;
  descendants?: (cb: (node: SigNode) => boolean | void) => void;
};

/** Block-node attrs that represent USER formatting (alignment / spacing / indent
 *  / heading style). Only these are folded into the signature — the docx node
 *  also carries identity + structural + layout attrs (paraId, textId, section
 *  properties, and potentially pagination-derived values) that change across
 *  non-content onChange events; serialising the whole attr bag would let those
 *  revive the "Saving…/Autosaved" resave loop the signature exists to prevent
 *  (MDRS-402 review). Mirrors para-clone's SAFE_PARA_ATTR_KEYS, minus the
 *  identity/structural ones. */
const BLOCK_FORMAT_ATTR_KEYS = new Set<string>([
  "alignment",
  "lineSpacing",
  "lineSpacingRule",
  "spacingExplicit",
  "spaceBefore",
  "spaceAfter",
  "contextualSpacing",
  "indentLeft",
  "indentRight",
  "indentFirstLine",
  "hangingIndent",
  "styleId",
  "outlineLevel",
]);

/** Stable key for a node's / mark's attrs — sorted so key order can't matter,
 *  null/undefined values dropped so absent === unset. When `allowed` is given,
 *  only those keys are included (block nodes pass the formatting whitelist so
 *  volatile attrs don't leak into the signature). */
const attrsKey = (
  attrs: Record<string, unknown> | null | undefined,
  allowed?: ReadonlySet<string>
): string => {
  if (!attrs) return "";
  let keys = Object.keys(attrs).filter((k) => attrs[k] != null);
  if (allowed) keys = keys.filter((k) => allowed.has(k));
  if (keys.length === 0) return "";
  keys.sort();
  return `{${keys.map((k) => `${k}=${String(attrs[k])}`).join(",")}}`;
};

/**
 * Content fingerprint used to tell a real edit from a zoom/selection/pagination
 * `onChange` (which the docx editor also fires). Base is `size:text` — size
 * catches structural edits, textContent catches typing.
 *
 * MDRS-405: `size:text` alone MISSED formatting-only edits. Applying bold /
 * italic / colour (inline marks) or alignment / line-spacing / indent (block
 * node attrs) changes neither the size nor the text, so the signature looked
 * identical to the last save and autosave never armed — the formatting was then
 * lost on reload. So we fold in a compact formatting fingerprint: each formatted
 * text run's marks, and each block node's formatting attrs. Pure selection / zoom
 * / pagination still yield an identical doc → identical fingerprint → still
 * ignored. The fingerprint is only appended when non-empty, so an unformatted
 * doc keeps the original `size:text` output. `null` doc → empty string.
 */
export const buildDocSignature = (doc: SigDoc | null | undefined): string => {
  if (!doc) return "";
  let fmt = "";
  doc.descendants?.((node) => {
    if (node.isText) {
      // Inline marks (bold / italic / underline / colour / highlight / link…).
      // Include the run length so moving a mark across differently-sized runs
      // registers as a change.
      if (node.marks && node.marks.length > 0) {
        fmt += `m${node.marks
          .map((mk) => mk.type.name + attrsKey(mk.attrs))
          .join("+")}#${node.text?.length ?? 0};`;
      }
    } else {
      // Block-level formatting lives in node attrs (alignment, line spacing,
      // indent, heading style). Only the whitelisted formatting attrs are folded
      // in — never identity/structural/layout attrs, which can change on a pure
      // zoom/pagination onChange and would resurrect the resave loop.
      const a = attrsKey(node.attrs, BLOCK_FORMAT_ATTR_KEYS);
      if (a) fmt += `${node.type?.name ?? "?"}${a};`;
    }
    return true;
  });
  const base = `${doc.content.size}:${doc.textContent}`;
  return fmt ? `${base}:${fmt}` : base;
};

/** ProseMirror node types whose binary the docx-editor's serializer drops
 *  on a save round-trip. The node reference survives in the saved XML but
 *  the underlying picture bytes don't end up in the zip's `word/media/`,
 *  so the next load renders an empty `<img>`. Skip the .docx upload
 *  whenever any of these are in the doc.
 *
 *  Verified against @eigenpal/docx-editor-core's bundled schema: `image`
 *  and `drawing` are the two node names that wrap a `<w:drawing>` /
 *  `<w:pict>` and rely on a binary in `word/media/`. Other media-shaped
 *  nodes (`shape`, `textBox`, `math`) don't reference an external binary
 *  in the same way and aren't included here; widen the set if QA reports
 *  a non-image embed losing its visuals on round-trip. */
export const MEDIA_NODE_TYPES = new Set(["image", "drawing"]);

/** True when the doc contains any media node the serializer can't preserve.
 *  Walks once and short-circuits on the first match. Accepts a minimal
 *  structural type so it can be unit-tested without spinning up a real
 *  ProseMirror state. */
export const docContainsMedia = (
  doc:
    | {
        descendants: (
          cb: (node: { type: { name: string } }) => boolean | void
        ) => void;
      }
    | null
    | undefined
): boolean => {
  if (!doc) return false;
  let found = false;
  doc.descendants((node) => {
    if (found) return false;
    if (MEDIA_NODE_TYPES.has(node.type.name)) {
      found = true;
      return false;
    }
  });
  return found;
};

export type EditChangeOutcome =
  /** First change after load — establish baseline, not a user edit. */
  | { kind: "baseline"; signature: string }
  /** Non-content mutation (zoom/selection/layout) — ignore. */
  | { kind: "ignore" }
  /** Real content edit — mark dirty + reschedule autosave. */
  | { kind: "edit"; signature: string };

/**
 * Decide what an `onChange` means, given the current doc signature and the
 * last-saved baseline (`null` until the first change after load).
 */
export const classifyEditChange = (
  signature: string,
  lastSavedSignature: string | null
): EditChangeOutcome => {
  if (lastSavedSignature === null) return { kind: "baseline", signature };
  if (signature === lastSavedSignature) return { kind: "ignore" };
  return { kind: "edit", signature };
};

/** Clamp a candidate Notes-panel width to its allowed range. */
export const clampNotesWidth = (next: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, next));

/**
 * Maximum Notes-panel width: half of the main container. Floored at `min` so
 * the clamp range stays valid on narrow containers (or a 0 width before the
 * container is measured), where half the width would fall below the minimum.
 */
export const notesMaxWidth = (containerWidth: number, min: number): number =>
  Math.max(min, containerWidth * 0.5);

/**
 * Relative "Autosaved …" label for a save timestamp `ts` given the current
 * time `now` (both ms). `null` ts → "" (never saved). Sub-minute → "just now".
 */
export const formatRelative = (ts: number | null, now: number): string => {
  if (!ts) return "";
  const mins = Math.floor((now - ts) / 60000);
  if (mins <= 0) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return hrs === 1 ? "1 hour ago" : `${hrs} hours ago`;
};
